import { Injectable } from '@angular/core';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import {
  ReferenceData,
  ReferenceDataCategoriesConstants,
  ReferenceDataConstants,
  ReferenceDataOptionLists,
  ReferenceDataService
} from 'rome-library';
import { UAParser } from 'ua-parser-js';
import {
  AllowedBrowser,
  Browser,
  Context,
  ContextPropertyType,
  Device,
  OperatingSystem,
  Resolution
} from '../models/browser-detector.model';

@Injectable({
  providedIn: 'root'
})
export class BrowserDetectorService {
  private readonly deviceInfo: DeviceInfo;
  private readonly android = 'android';
  private readonly mobileType = 'mobile';
  private readonly tabletType = 'tablet';

  private isDeviceAllowedFlag: boolean;
  private isPhoneBlacklistedFlag: boolean;
  private allowedBrowsers: Array<AllowedBrowser> = [];
  private blacklistedDevices: Array<Device> = [];
  private readonly blacklistedPhoneContexts: Array<Context> = [];
  private minimumMobileDeviceResolution: Resolution;
  private readonly uaParser: UAParser;

  public constructor(
    private readonly deviceService: DeviceDetectorService,
    private readonly referenceDataService: ReferenceDataService
  ) {
    this.deviceInfo = this.deviceService.getDeviceInfo();
    this.uaParser = new UAParser();
    this.loadConfiguration();
  }

  public isMobileDeviceAllowed(): boolean {
    if (this.isDeviceAllowedFlag === undefined) {
      this.isDeviceAllowedFlag = this.isDeviceAllowed();
    }

    return this.isDeviceAllowedFlag;
  }

  public isPhoneContextBlacklisted(): boolean {
    if (this.isPhoneBlacklistedFlag === undefined) {
      this.isPhoneBlacklistedFlag = this.isCurrentPhoneContextBlacklisted();
    }

    return this.isPhoneBlacklistedFlag;
  }

  public isAndroidDevice(): boolean {
    return this.getDeviceOS().name.toLowerCase() === this.android;
  }

  public isPhoneDevice(): boolean {
    return this.uaParser.getDevice().type === this.mobileType;
  }

  public isTabletDevice(): boolean {
    return this.uaParser.getDevice().type === this.tabletType;
  }

  public isMobileDevice(): boolean {
    return this.isPhoneDevice() || this.isTabletDevice() || this.isAndroidDevice();
  }

  public isBrowserAllowed(): boolean {
    const browser = this.getDeviceBrowser();
    const userAgent = this.getDeviceUserAgent();

    return this.allowedBrowsers.some((allowedBrowser: AllowedBrowser): boolean => {
      if (this.normalizeBrowserName(allowedBrowser.browser) === this.normalizeBrowserName(browser.name)) {
        const minimumVersionCheck = this.compareBrowserVersions(allowedBrowser.minimumVersion, browser.version);
        const maximumVersionCheck = this.compareBrowserVersions(browser.version, allowedBrowser.maximumVersion);

        const isBrowserVersionAllowed =
          (minimumVersionCheck === -1 || minimumVersionCheck === 0) &&
          (maximumVersionCheck === -1 || maximumVersionCheck === 0);

        const areOptionalValuesValid = this.compareUserAgentString(userAgent, allowedBrowser.optionalValues);

        return isBrowserVersionAllowed && areOptionalValuesValid;
      }

      return false;
    });
  }

  public checkIfDeviceIsNotAllowedForActualConfiguration(deviceUnsupportedCallback: () => void): void {
    const allowPhones = this.referenceDataService.getClientConfigurationValue(
      ReferenceDataConstants.ClientEnvironmentAllowPhones
    );
    const allowTablets = this.referenceDataService.getClientConfigurationValue(
      ReferenceDataConstants.ClientEnvironmentAllowTablets
    );

    if (this.isPhoneDevice() && allowPhones === ReferenceDataOptionLists.AnswerTypeNo) {
      deviceUnsupportedCallback();

      return;
    }

    if (this.isTabletDevice() && allowTablets === ReferenceDataOptionLists.AnswerTypeNo) {
      deviceUnsupportedCallback();
    }
  }

  public checkUnsupportedDevice(deviceUnsupportedCallback: () => void): void {
    if (this.isMobileDevice()) {
      if (!this.isMobileDeviceAllowed() || (this.isPhoneDevice() && this.isPhoneContextBlacklisted())) {
        deviceUnsupportedCallback();
      }
    } else if (!this.isBrowserAllowed()) {
      deviceUnsupportedCallback();
    }
  }

  private normalizeBrowserName(browserName: string): string {
    return browserName.toLowerCase().replace(/^ms\-/, '');
  }

  private compareBrowserVersions(v1: string, v2: string): Number {
    const v1StringParts = v1.split('.');
    const v2StringParts = v2.split('.');

    while (v1StringParts.length < v2StringParts.length) {
      v1StringParts.push('0');
    }

    while (v2StringParts.length < v1StringParts.length) {
      v2StringParts.push('0');
    }

    const v1Parts = v1StringParts.map(Number);
    const v2Parts = v2StringParts.map(Number);

    for (let partIndex = 0; partIndex < v1Parts.length; partIndex += 1) {
      if (v2Parts.length === partIndex) {
        return 1;
      }

      if (v1Parts[partIndex] === v2Parts[partIndex]) {
        continue;
      } else if (v1Parts[partIndex] > v2Parts[partIndex]) {
        return 1;
      }

      return -1;
    }

    return 0;
  }

  private compareUserAgentString(userAgentString: string, optionalValues: Array<string>): boolean {
    return optionalValues.every((value: string): boolean => {
      return userAgentString.toLowerCase().includes(value.toLowerCase());
    });
  }

  private getCurrentResolution(): Resolution {
    const devicePixelRatio = window.devicePixelRatio;

    return new Resolution({
      width: window.screen.width * devicePixelRatio,
      height: window.screen.height * devicePixelRatio
    });
  }

  private getCurrentResolutionsByOrientations(): Array<Resolution> {
    return [
      new Resolution({ width: this.getCurrentResolution().width, height: this.getCurrentResolution().height }),
      new Resolution({ width: this.getCurrentResolution().height, height: this.getCurrentResolution().width })
    ];
  }

  private getDeviceBrowser(): Browser {
    return {
      name: this.deviceInfo.browser,
      version: this.deviceInfo.browser_version
    };
  }

  private getDeviceOS(): OperatingSystem {
    return {
      name: this.deviceInfo.os,
      version: this.deviceInfo.os_version
    };
  }

  private getDeviceUserAgent(): string {
    return this.deviceInfo.userAgent;
  }

  private isDeviceAllowed(): boolean {
    if (this.isMobileDevice()) {
      const deviceFitsResolution = this.isDeviceFittingMinimumResolution();

      if (deviceFitsResolution) {
        return !this.isMobileDeviceBlacklisted();
      }

      return false;
    }

    return true;
  }

  private isDeviceFittingMinimumResolution(): boolean {
    const resolutions = this.getCurrentResolutionsByOrientations();
    let fitsResolution = false;

    return resolutions.some((resolution: Resolution): boolean => {
      fitsResolution =
        resolution.width >= this.minimumMobileDeviceResolution.width &&
        resolution.height >= this.minimumMobileDeviceResolution.height;

      return fitsResolution;
    });
  }

  private isMobileDeviceBlacklisted(): boolean {
    if (this.blacklistedDevices.length === 0) {
      return false;
    }

    const currentUserAgentString = this.getDeviceUserAgent().toLowerCase();

    return this.blacklistedDevices.some((device: Device): boolean => {
      return currentUserAgentString.includes(device.userAgent.toLowerCase());
    });
  }

  private contextPropertyCoincides(contextProperty: ContextPropertyType, configProperty: ContextPropertyType): boolean {
    if (this.normalizeBrowserName(contextProperty.name) === this.normalizeBrowserName(configProperty.name)) {
      if (configProperty.version) {
        return contextProperty.version.startsWith(configProperty.version);
      }

      return true;
    }

    return false;
  }

  private isCurrentPhoneContextBlacklisted(): boolean {
    if (this.blacklistedPhoneContexts.length === 0) {
      return false;
    }

    const os = this.getDeviceOS();
    const browser = this.getDeviceBrowser();

    return this.blacklistedPhoneContexts.some((config: Context): boolean => {
      if (!config.os.name) {
        return this.contextPropertyCoincides(browser, config.browser);
      }

      if (!config.browser.name) {
        return this.contextPropertyCoincides(os, config.os);
      }

      return this.contextPropertyCoincides(os, config.os) && this.contextPropertyCoincides(browser, config.browser);
    });
  }

  private loadConfiguration(): void {
    const mapKeySeparator = this.referenceDataService.mapKeySeparator;

    this.allowedBrowsers = [];
    this.blacklistedDevices = [];
    this.minimumMobileDeviceResolution = undefined;
    let categoryName: string;

    this.referenceDataService.referenceData.forEach((configRefData: Array<ReferenceData>, mapKey: string): void => {
      categoryName = mapKey.split(mapKeySeparator)[1];

      if (mapKey.startsWith(`${ReferenceDataCategoriesConstants.ALLOWED_BROWSERS}${mapKeySeparator}`)) {
        this.loadAllowedBrowsers(categoryName, configRefData);
      }

      if (mapKey.startsWith(`${ReferenceDataCategoriesConstants.BLOCKED_MOBILE_USER_AGENT}${mapKeySeparator}`)) {
        this.loadBlackListedDevices(categoryName, configRefData);
      }

      if (mapKey.startsWith(`${ReferenceDataCategoriesConstants.CLIENT_ENVIRONMENT}${mapKeySeparator}`)) {
        this.loadMinimumMobileDeviceResolution(categoryName, configRefData);
      }

      if (mapKey.startsWith(`${ReferenceDataCategoriesConstants.PHONE_CONTEXT_BLACKLIST}${mapKeySeparator}`)) {
        this.loadPhoneContextBlacklist(configRefData);
      }
    });
  }

  private loadAllowedBrowsers(browserName: string, allowedBrowsersSection: Array<ReferenceData>): void {
    allowedBrowsersSection.forEach((browser: ReferenceData): void => {
      this.allowedBrowsers.push(new AllowedBrowser(browserName, browser.Value));
    });
  }

  private loadBlackListedDevices(blockedDeviceName: string, blockedDevicesSection: Array<ReferenceData>): void {
    blockedDevicesSection.forEach((device: ReferenceData): void => {
      this.blacklistedDevices.push(new Device({ name: blockedDeviceName, userAgent: device.Value }));
    });
  }

  private loadMinimumMobileDeviceResolution(
    environmentSettingName: string,
    environmentSection: Array<ReferenceData>
  ): void {
    if (environmentSettingName === ReferenceDataConstants.ClientEnvironmentMobileMinimumResolution) {
      const resolution = environmentSection[0].Value.toLowerCase().split('x');
      this.minimumMobileDeviceResolution = new Resolution({
        width: Number(resolution[0]),
        height: Number(resolution[1])
      });
    }
  }

  private loadPhoneContextBlacklist(phoneBlacklistSection: Array<ReferenceData>): void {
    phoneBlacklistSection.forEach((item: ReferenceData): void => {
      const browser = item.getAttributeValue(ReferenceDataConstants.PhoneContextBlacklistBrowserAttribute);
      const os = item.getAttributeValue(ReferenceDataConstants.PhoneContextBlacklistOSAttribute);

      if (browser || os) {
        this.blacklistedPhoneContexts.push(new Context(browser, os));
      }
    });
  }
}
